import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { selectUser } from "store/features/auth/authSlice";
import {
  useGetAllMoneyReceiptsQuery,
  useDeleteMoneyReceiptMutation,
} from "store/features/invoice/moneyReceiptApi";

const AllMoneyReceipts = () => {
  const user = useSelector(selectUser);

  const { data: receipts, isLoading } = useGetAllMoneyReceiptsQuery();
  const [deleteReceipt, { isLoading: deletingReceipt }] = useDeleteMoneyReceiptMutation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedReceiptId, setSelectedReceiptId] = useState(null);

  const showDeleteModal = (id) => {
    setSelectedReceiptId(id);
    setDeleteModal(true);
  };

  const handleDeleteReceipt = async () => {
    try {
      await deleteReceipt(selectedReceiptId).unwrap();
      toast.success("Money receipt deleted successfully");
      setDeleteModal(false);
    } catch (error) {
      toast.error("Failed to delete money receipt");
    }
  };

  // Get client name from receipt data
  const getClientName = (receiptData) => {
    return receiptData?.clientName || "N/A";
  };

  // Get total amount from receipt data
  const getTotalAmount = (receiptData) => {
    return receiptData?.totalAmount || 0;
  };

  // Get received amount from receipt data
  const getReceivedAmount = (receiptData) => {
    return receiptData?.receivedAmount || 0;
  };

  // Get payment status badge
  // const getPaymentStatusBadge = (status) => {
  //   const statusConfig = {
  //     "PAID IN FULL": "success",
  //     "PARTIALLY PAID": "warning",
  //     "PLEASE COMPLETE THE PENDING PAYMENT": "warning",
  //     "PENDING": "danger",
  //   };
  //   const color = statusConfig[status] || "secondary";
  //   return `<span class="badge bg-${color}">${status}</span>`;
  // };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      "PAID IN FULL": "success",
      "PARTIALLY PAID": "warning",
      "PENDING": "danger",
    };

    // If status is "PLEASE COMPLETE THE PENDING PAYMENT", show "PARTIALLY PAID" instead
    let displayStatus = status;
    if (status === "PLEASE COMPLETE THE PENDING PAYMENT") {
      displayStatus = "PARTIALLY PAID";
    }

    const color = statusConfig[displayStatus] || "secondary";
    return `<span class="badge bg-${color}">${displayStatus}</span>`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: "Receipt No",
        accessorKey: "receipt_no",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">{row.original.receipt_no}</div>
          </div>
        ),
      },
      {
        header: "Client Name",
        accessorKey: "receipt_data",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => getClientName(row.original.receipt_data),
      },
      {
        header: "Total Amount",
        accessorKey: "receipt_data",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const total = getTotalAmount(row.original.receipt_data);
          return <span>BDT {total.toLocaleString()}</span>;
        },
      },
      {
        header: "Received Amount",
        accessorKey: "receipt_data",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const received = getReceivedAmount(row.original.receipt_data);
          return <span className="text-success">BDT {received.toLocaleString()}</span>;
        },
      },
      {
        header: "Payment Status",
        accessorKey: "payment_status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div
            dangerouslySetInnerHTML={{
              __html: getPaymentStatusBadge(row.original.payment_status),
            }}
          />
        ),
      },
      {
        header: "Sent To",
        accessorKey: "sent_to",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.sent_to || "N/A",
      },
      {
        header: "Sent Date",
        accessorKey: "sent_at",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.sent_at),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="d-flex align-items-center gap-1">
            <Link
              to={`/money-receipt/${row.original.id}`}
              className="btn btn-info btn-sm"
            >
              <i className="fas fa-eye me-1"></i> View
            </Link>

            {(user?.role === "super_admin" || user?.role === "admin") && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => showDeleteModal(row.original.id)}
              >
                <i className="fas fa-trash me-1"></i>
                {deletingReceipt ? "Deleting..." : "Delete"}
              </button>
            )}
          </div>
        ),
      },
    ];

    return baseColumns;
  }, [deletingReceipt, user]);

  if (isLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Money Receipts" breadcrumbItem="All Money Receipts" />

        <TableContainer
          columns={columns}
          data={receipts || []}
          isGlobalFilter={true}
          isPagination={true}
          SearchPlaceholder="Search Money Receipts..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteReceipt}
          loader={deletingReceipt}
          onCloseClick={() => setDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default AllMoneyReceipts;